import React, { useState, useEffect, useRef } from 'react';
import MetaTags from 'react-meta-tags';
import { Container, Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';

import { useAuth } from 'hooks/useAuth';
import Breadcrumbs from '../../components/Breadcrumb';
import SuccessModal from '../../components/Success-Modal';
import LabelGroup from '../../components/Label-Group';
import TextInput from '../../components/Text-Input';
import DateInput from '../../components/Date-Input';
import MarkdownInput from '../../components/Markdown-Input';
import SelectInput from '../../components/Select-Input';
import SpinnerInput from '.../../components/Spinner-Input';
import NextSteps from '../../components/Next-Steps';
import SubmitButton from '../../components/Submit-Button';
import ParticipantsSelect from './components/Participants-Select';
import {
  getFormValue,
  getEpochDate,
  loadTeam,
  normalizeSteps,
  loadExternalTasks,
} from '../../utils/';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import '../../assets/scss/custom/components/_dragndrop.scss';

const MeetingsCreate = () => {
  const formConfig = {
    defaultValues: {
      agenda: '',
      date: '',
      description: '',
      participants: [],
      relatedTasks: [],
      videoLink: '',
      steps: [{ assignedTo: '', step: '' }],
      minutes: 60,
    },
    shouldFocusError: true,
    criteriaMode: 'all',
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  };

  const firebaseHelper = getFirebaseBackend();

  const form = useForm(formConfig);
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const accountId = user.account;
  const [meetingId, setMeetingId] = useState();
  const [successModal, setSuccessModal] = useState(false);
  const [assignedKey, setAssignedKey] = useState(0);
  const [tasksKey, setTasksKey] = useState(2000);
  const [isSending, setIsSending] = useState(false);
  const meetingIdRef = useRef();

  const { clearErrors, formState } = form;
  meetingIdRef.current = meetingId;

  const navigate = useNavigate();

  const closeModalHandle = () => {
    form.reset();
    setSuccessModal(false);
  };

  const onSubmit = async (data) => {
    try {
      setIsSending(true);

      const meeting = {
        id: meetingId,
        accountId,
        agenda: data.agenda,
        description: data.description !== '' ? data.description : null,
        date: getEpochDate(data.date),
        minutes: data.minutes,
        videoLink: data.videoLink,
        steps: normalizeSteps(data.steps),
        participants: data.participants.map(getFormValue),
        relatedTasks: data.relatedTasks ? data.relatedTasks.map(getFormValue) : null,
      };

      if (data.description.length !== 0) {
        clearErrors('description');
      }
      if (data.participants.length !== 0) {
        clearErrors('participants');
      }

      await firebaseHelper.registerMeeting(meeting);
      // Get new Task Id for the next possible task to be created
      const newMeetingKey = firebaseHelper.newMeetingKey(accountId);
      setMeetingId((prevState) => newMeetingKey);

      setSuccessModal(true);
      setIsSending(false);
    } catch (error) {
      console.error(error);
      setIsSending(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      if (user.adminStatus !== 'Admin' && user.adminStatus !== 'Super Admin') {
        navigate('/');
      }

      const currentMeetingId = await firebaseHelper.newMeetingKey(accountId);
      setMeetingId(currentMeetingId);

      return () => {
        firebaseHelper.removeUnusedMDFiles({
          accountId,
          id: meetingIdRef.current,
        });
      };
    };
    initialize();
  }, []);

  useEffect(() => {
    setAssignedKey((prevState) => prevState + 1);
    setTasksKey((prevState) => prevState + 1);
  }, [accountId]);

  return (
    <>
      <div className="page-content">
        <MetaTags>
          <title>{`${translate('Create Meeting')} | Ectools`}</title>
        </MetaTags>
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title={translate('Meetings')} breadcrumbItem={translate('Create Meeting')} />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <SuccessModal
                    isOpen={successModal}
                    onClose={closeModalHandle}
                    verb="Created"
                    buttonText="See Meetings"
                    buttonLink="/meetings"
                  />

                  <CardTitle className="mb-4">{translate('Create New Meeting Log')}</CardTitle>

                  <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <LabelGroup htmlFor="date" label="Date" required>
                        <DateInput
                          controlName="date"
                          placeholder="Meeting Date"
                          size={4}
                          required
                        />
                        <SpinnerInput
                          controlName="minutes"
                          label="Minutes Long"
                          required
                          size={4}
                          min={1}
                        />
                      </LabelGroup>

                      <LabelGroup htmlFor="agenda" label="Meeting Agenda" required>
                        <TextInput controlName="agenda" placeholder="Meeting Agenda" required />
                      </LabelGroup>

                      <LabelGroup htmlFor="description" label="Description" required>
                        <MarkdownInput attachId={meetingId} controlName="description" required />
                      </LabelGroup>

                      <NextSteps
                        key={assignedKey}
                        cacheOptions
                        defaultOptions
                        loadOptions={(inputValue) => loadTeam(accountId, inputValue)}
                      />

                      <ParticipantsSelect accountId={accountId} />

                      <LabelGroup htmlFor="videoLink" label="Video Link">
                        <TextInput
                          controlName="videoLink"
                          placeholder="Type the Meeting Video URL (Vimeo or Youtube)"
                          example="https://vimeo.com/1234567890"
                          // Regex pattern to only match vimeo and youtube URLS
                          validation={{
                            pattern: {
                              value:
                                /(https?:\/\/(.+?\.)?(vimeo|youtube)\.com(\/[A-Za-z0-9\-\._~:\/\?#\[\]@!$&'\(\)\*\+,;\=]*)?)/,
                              message: 'The URL needs to be from Vimeo or Youtube',
                            },
                          }}
                        />
                      </LabelGroup>

                      <LabelGroup htmlFor="relatedTasks" label="Related Tasks">
                        <SelectInput
                          asyncronous
                          isMulti
                          controlName="relatedTasks"
                          selectedKey={tasksKey}
                          cacheOptions
                          defaultOptions
                          loadOptions={(inputValue) => loadExternalTasks(accountId, inputValue)}
                        />
                      </LabelGroup>

                      <SubmitButton
                        text="Create Log"
                        disabled={Boolean(Object.values(formState.errors).length)}
                        isLoading={isSending}
                      />
                    </form>
                  </FormProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default MeetingsCreate;
